<template>
    <div :class="expandLoadingClass">
        <v-inline-loader v-if="isLoading"/>
        <div v-if="hasRows">
            <table class="table" :class="tableClass" >
                <slot name="colgroup"/>
                <thead>
                    <tr>
                        <slot name="headers"/>
                    </tr>
                </thead>
                <tbody>
                <tr
                    :class="rowClass"
                    v-for="(item, itemIdx) in rowsToShow"
                    @click="itemClickDelegate(item, index)"
                    :key="getIndex(item, index)">
                    <slot name="iteratee" :item="item" :index="itemIdx"/>
                </tr>
                </tbody>
            </table>
            <v-pagination
                v-if="requiresPagination"
                :limit="cachedProviderParams.limit"
                :offset="cachedProviderParams.offset"
                :total="totalRecords"
                @pagination-previous="movePageBackward"
                @pagination-next="movePageForward"/>
        </div>
        <div v-else>
            <slot name="no-content"/>
        </div>
        <template v-if="showToggleButtons">
            <div class="divider"></div>
            <div class="text-center">
                <r-button v-if="showMore" @click="showMore = false">Show more {{slice.label}}</r-button>
                <r-button v-else @click="showMore = true">Show less {{slice.label}}</r-button>
            </div>
        </template>
    </div>

</template>

<script>
    import vPagination from '@/components/v-pagination';
    import vInlineLoader from '@/components/v-inline-loader';

    export default {
        name: 'repeater',
        components: {
            vPagination, vInlineLoader,
        },
        props: {
            params: {
                type: Object,
                default: () => ({
                    limit: 20,
                    offset: 0,
                }),
            },
            index: {
                type: Function,
                default: null,
            },
            list: {
                type: Array,
                default: null,
            },
            provider: {
                type: Function,
                default: null,
            },
            pagination: {
                type: Boolean,
                default: false,
            },
            itemClick: {
                type: Function,
                default: null,
            },
            tableClass: {
                type: String,
                default: null,
            },
            rowClass: {
                type: String,
                default: null,
            },
            expandLoading: {
                type: Boolean,
                default: false,
            },
            slice: {
                type: Object,
                default: null,
            },
            mapRows: {
                type: Function,
                default: null,
            },
        },
        data() {
            return {
                rows: [],
                totalRecords: 0,
                cachedProviderParams: {
                    limit: this.params.limit || 20,
                    offset: this.params.offset || 0,
                },
                showMore: true,
                isLoading: true,
            };
        },
        watch: {
            list: {
                handler(value) {
                    if (!this.provider) {
                        this.rows = value;
                        this.isLoading = false;
                    }
                },
                immediate: true,
            },
        },
        computed: {
            hasRows() {
                return this.rows.length > 0;
            },
            requiresPagination() {
                return this.pagination && this.rows.length && this.totalRecords > this.cachedProviderParams.limit;
            },
            expandLoadingClass() {
                return {'position-relative': !this.expandLoading};
            },
            rowsToShow() {
                return this.slice && this.rows.length > this.slice.value && this.showMore
                    ? this.rows.slice(0, this.slice.value) : this.rows;
            },
            showToggleButtons() {
                return this.slice && this.rows.length > this.slice.value;
            },
            rowsMapper() {
                return this.mapRows || (items => items.map(item => item.fields));
            },
        },
        methods: {
            getIndex(item, index) {
                if (this.index) {
                    return this.index.call(null, item, index);
                }
                return index;
            },
            async fetchData() {
                this.isLoading = true;
                try {
                    const {items, total} = await this.provider(this.cachedProviderParams);

                    if (this.cachedProviderParams.offset >= total && this.cachedProviderParams.offset > 0) {
                        while (this.cachedProviderParams.offset >= total && this.cachedProviderParams.offset > 0) {
                            this.cachedProviderParams.offset = Math.max(this.cachedProviderParams.offset - this.cachedProviderParams.limit, 0);
                        }
                        await this.fetchData();
                        return;
                    }

                    this.totalRecords = Number(total);
                    this.rows = this.rowsMapper(items);
                } catch (error) {
                    this.serverError = true;
                } finally {
                    this.isLoading = false;
                    this.$emit('update', this.rows);
                }
            },
            itemClickDelegate(item, index) {
                if (this.itemClick) {
                    this.itemClick.call(null, item, index);
                }
            },
            movePageBackward() {
                if (this.cachedProviderParams.offset >= this.cachedProviderParams.limit) {
                    this.cachedProviderParams.offset -= this.cachedProviderParams.limit;
                    this.fetchData();
                }
            },
            movePageForward() {
                if (this.cachedProviderParams.offset < this.totalRecords) {
                    this.cachedProviderParams.offset += this.cachedProviderParams.limit;
                    this.fetchData();
                }
            },
        },
        mounted() {
            if (this.provider) {
                this.fetchData();
            }
        },
    };
</script>

<style scoped>
</style>
